import { debounce } from "lodash-es";
import { onUnmounted, ref, watch, type WatchSource } from "vue";

const AUTO_SELECT_DEBOUNCE_MS = 500;

/**
 * The auto-select toggle and target, and when to fill the target: at once when auto-select is
 * ticked, then again (debounced) on every new target or change in `sources` while it stays on.
 * `run` is only called while auto-select is on and a target is set.
 */
export function useAutoSelect(options: {
  run: (targetMwh: number) => void;
  sources: WatchSource[];
}) {
  const enabled = ref(false);
  const targetMwh = ref<number | null>(null);

  const runNow = () => {
    if (enabled.value && targetMwh.value) options.run(targetMwh.value);
  };
  const debouncedRun = debounce(runNow, AUTO_SELECT_DEBOUNCE_MS);

  watch(enabled, (on) => {
    if (!on) return;
    debouncedRun.cancel();
    runNow();
  });

  watch([targetMwh, ...options.sources], () => {
    if (enabled.value) debouncedRun();
  });

  onUnmounted(() => debouncedRun.cancel());

  return { enabled, targetMwh };
}
